import {
  POS_DEFAULT_TIMEZONE,
  POS_TIMEZONES,
  POS_SUBSCRIPTION_STATUS,
  POS_SUBSCRIPTION_WARN_DAYS,
} from '../constants/posConst';

// Calendar arithmetic for subscriptions.
//
// EVERY DATE HERE IS A PLAIN 'YYYY-MM-DD' STRING, never a Date or a timestamp.
// A subscription runs "from the 1st to the 30th" in the club's own timezone,
// and a timestamp would drag the browser's timezone into that — a plan viewed
// from a laptop set to another zone would appear to end a day early or late.
//
// The arithmetic is done on UTC midnights (Date.UTC), where there are no
// daylight-saving jumps, so adding a day always adds exactly one day. The
// club's timezone is used for ONE thing only: deciding what "today" is.

const DAY_MS = 24 * 60 * 60 * 1000;
const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Whether the browser recognises an IANA timezone name.
 *
 * @param {string} timeZone - e.g. 'Asia/Manila'
 * @returns {boolean}
 */
export const isValidTimeZone = (timeZone) => {
  if (!timeZone) return false;
  try {
    Intl.DateTimeFormat('en-US', { timeZone });
    return true;
  } catch {
    return false;
  }
};

/**
 * A usable timezone for a client: its own if valid, otherwise the default.
 *
 * @param {string} [timeZone]
 * @returns {string}
 */
export const resolveTimeZone = (timeZone) => (isValidTimeZone(timeZone) ? timeZone : POS_DEFAULT_TIMEZONE);

/**
 * Whether a string is a real calendar date in 'YYYY-MM-DD' form.
 *
 * Round-tripped through Date.UTC so '2026-02-30' is refused rather than
 * silently becoming 2 March.
 *
 * @param {string} value
 * @returns {boolean}
 */
export const isValidDateString = (value) => {
  const match = DATE_PATTERN.exec(String(value || ''));
  if (!match) return false;
  const [, y, m, d] = match.map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d;
};

/**
 * 'YYYY-MM-DD' -> UTC-midnight milliseconds.
 *
 * @param {string} value
 * @returns {number}
 */
const toUtcMs = (value) => {
  const [, y, m, d] = DATE_PATTERN.exec(value).map(Number);
  return Date.UTC(y, m - 1, d);
};

/**
 * UTC-midnight milliseconds -> 'YYYY-MM-DD'.
 *
 * @param {number} ms
 * @returns {string}
 */
const fromUtcMs = (ms) => new Date(ms).toISOString().slice(0, 10);

/**
 * Today's date where the club is.
 *
 * 'en-CA' is used only because it formats as YYYY-MM-DD; nothing about Canada
 * is implied.
 *
 * @param {string} [timeZone] - IANA name; falls back to POS_DEFAULT_TIMEZONE.
 * @param {Date} [now] - injectable for tests.
 * @returns {string} 'YYYY-MM-DD'
 */
export const todayInTimeZone = (timeZone, now = new Date()) => new Intl.DateTimeFormat('en-CA', {
  timeZone: resolveTimeZone(timeZone),
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
}).format(now);

/**
 * Adds (or with a negative count, subtracts) whole days.
 *
 * @param {string} date - 'YYYY-MM-DD'
 * @param {number} days
 * @returns {string}
 */
export const addDays = (date, days) => fromUtcMs(toUtcMs(date) + days * DAY_MS);

/**
 * Adds whole months, clamping to the end of a shorter month.
 *
 * Clamped rather than rolled over: one month from 31 January is 28/29
 * February, not 3 March — rolling over would quietly hand the club extra days.
 *
 * @param {string} date - 'YYYY-MM-DD'
 * @param {number} months
 * @returns {string}
 */
export const addMonths = (date, months) => {
  const [, y, m, d] = DATE_PATTERN.exec(date).map(Number);
  const targetMonthIndex = (m - 1) + months;
  const lastDay = new Date(Date.UTC(y, targetMonthIndex + 1, 0)).getUTCDate();
  return fromUtcMs(Date.UTC(y, targetMonthIndex, Math.min(d, lastDay)));
};

/**
 * Whole days from one date to another. Positive when `to` is later.
 *
 * @param {string} from - 'YYYY-MM-DD'
 * @param {string} to - 'YYYY-MM-DD'
 * @returns {number}
 */
export const daysBetween = (from, to) => Math.round((toUtcMs(to) - toUtcMs(from)) / DAY_MS);

/**
 * The LAST day a plan covers, counting its first day as day one.
 *
 * Inclusive on purpose: a 30-day plan starting on the 1st covers the 1st to
 * the 30th, and "ends on the 30th" is what the club and the receipt both say.
 *
 * @param {string} startDate - 'YYYY-MM-DD'
 * @param {number} durationValue - e.g. 30, or 1
 * @param {string} durationUnit - 'days' or 'months'
 * @returns {string} 'YYYY-MM-DD'
 */
export const planEndDate = (startDate, durationValue, durationUnit) => {
  const count = Math.max(1, Math.floor(Number(durationValue) || 1));
  if (durationUnit === 'months') {
    const sameDayNextPeriod = addMonths(startDate, count);
    // Clamped (31 Jan -> 28 Feb): the shorter month has no "same day", so the
    // period runs to that month's last day rather than stopping one short.
    if (sameDayNextPeriod.slice(8) !== startDate.slice(8)) return sameDayNextPeriod;
    return addDays(sameDayNextPeriod, -1);
  }
  return addDays(startDate, count - 1);
};

/**
 * Where a new period should start when a client renews.
 *
 * A client still inside a paid period is extended from the day after it ends,
 * so paying early never costs them the days they had left. Anyone else starts
 * today.
 *
 * @param {object|null} current - the client's current subscription
 * @param {string} timeZone
 * @returns {string} 'YYYY-MM-DD'
 */
export const nextStartDate = (current, timeZone) => {
  const today = todayInTimeZone(timeZone);
  if (current?.endDate && isValidDateString(current.endDate) && current.endDate >= today) {
    return addDays(current.endDate, 1);
  }
  return today;
};

/**
 * Where a subscription stands on the club's today.
 *
 * `daysLeft` is 0 on the last day — the plan still covers today, but not
 * tomorrow — and negative once it has lapsed.
 *
 * @param {object|null} subscription - { startDate, endDate, ... }
 * @param {string} [timeZone]
 * @param {string} [today] - injectable for tests
 * @returns {{status: string, daysLeft: number|null, today: string}}
 */
export const getSubscriptionState = (subscription, timeZone, today = todayInTimeZone(timeZone)) => {
  if (!subscription || !isValidDateString(subscription.endDate)) {
    return { status: POS_SUBSCRIPTION_STATUS.NONE, daysLeft: null, today };
  }
  const daysLeft = daysBetween(today, subscription.endDate);
  if (isValidDateString(subscription.startDate) && subscription.startDate > today) {
    return { status: POS_SUBSCRIPTION_STATUS.SCHEDULED, daysLeft, today };
  }
  if (daysLeft < 0) return { status: POS_SUBSCRIPTION_STATUS.EXPIRED, daysLeft, today };
  if (daysLeft <= POS_SUBSCRIPTION_WARN_DAYS) return { status: POS_SUBSCRIPTION_STATUS.EXPIRING, daysLeft, today };
  return { status: POS_SUBSCRIPTION_STATUS.ACTIVE, daysLeft, today };
};

/**
 * A date for people to read, e.g. 'Sep 24, 2026'.
 *
 * Formatted in UTC because the string already IS the club's date; formatting
 * it in the browser's zone could move it by a day.
 *
 * @param {string} date - 'YYYY-MM-DD'
 * @returns {string} '' for a missing or malformed date
 */
export const formatPlanDate = (date) => {
  if (!isValidDateString(date)) return '';
  return new Date(toUtcMs(date)).toLocaleDateString('en-US', {
    timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric',
  });
};

/**
 * A short name for a timezone, printed beside subscription dates — e.g.
 * 'PH Time' for Asia/Manila.
 *
 * Zones outside POS_TIMEZONES fall back to the browser's own short name for
 * them (e.g. 'GMT+8'), and finally to the IANA name itself.
 *
 * @param {string} [timeZone]
 * @returns {string}
 */
export const formatTimeZoneLabel = (timeZone) => {
  const zone = resolveTimeZone(timeZone);
  const known = POS_TIMEZONES.find((entry) => entry.key === zone);
  if (known?.short) return known.short;
  try {
    const part = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'short' })
      .formatToParts(new Date())
      .find((entry) => entry.type === 'timeZoneName');
    return part?.value || zone;
  } catch {
    return zone;
  }
};

/**
 * A date with its timezone, e.g. 'Sep 26, 2026 (PH Time)'.
 *
 * @param {string} date - 'YYYY-MM-DD'
 * @param {string} [timeZone]
 * @returns {string} '' for a missing or malformed date
 */
export const formatPlanDateInZone = (date, timeZone) => {
  const formatted = formatPlanDate(date);
  return formatted ? `${formatted} (${formatTimeZoneLabel(timeZone)})` : '';
};

/**
 * A plan's length for people to read, e.g. '30 days', '1 month'.
 *
 * @param {number} value
 * @param {string} unit - 'days' or 'months'
 * @returns {string}
 */
export const formatPlanDuration = (value, unit) => {
  const count = Math.max(1, Math.floor(Number(value) || 1));
  const noun = unit === 'months' ? 'month' : 'day';
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
};
